"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { House as Home, Building2, HardHat, ArrowRight, ShieldCheck, Flame, Umbrella, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const propertyTypes = ["Town-centre commercial properties", "Office developments", "Residential & conservation areas", "Refurbishment & mixed-use buildings", "Specialist roof projects"];
const residentialFeatures = ["Roof repairs & replacements", "Single & multi-storey extensions", "Chimney stack repairs & repointing", "External wall rendering & painting", "Guttering, soffits & fascia replacements"];
const commercialFeatures = ["Main contractor & developer partnerships", "High-street retail & office refurbishment", "Occupied site access with public protection", "Temporary roofs for weatherproofing", "Detailed RAMS & weekly inspections"];
const tradeAccessCards = [{
  title: "Roofing scaffolding",
  description: "Multi-level perimeter access designed for roofers, tilers, and solar installers with hoist beams.",
  to: "/RoofingScaffoldingPage",
  icon: HardHat,
  tag: "Trade access"
}, {
  title: "Temporary roofs",
  description: "Fully weatherproof corrugated tin and shrink-wrap canopies for complete structural protection.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella,
  tag: "Weatherproofing"
}, {
  title: "Edge protection",
  description: "Compliant handrails, toe-boards, and barrier systems preventing falls from height during roof work.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck,
  tag: "Safety compliance"
}];
export default function LocalServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localcoverageservices" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mb-12 lg:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wide gap-1.5 px-3 py-1 font-semibold">
              <MapPin className="size-3.5 text-primary" />
              Marlow & South East Coverage
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Scaffolding access across Marlow and surrounding areas
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From riverside townhouses and high-street shopfronts to commercial business parks, we design, erect, and inspect reliable access systems tailored to local building constraints.
          </p>
        </motion.div>

        {/* Property types ticker / pills */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="mb-14 p-4 sm:p-5 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
              Sites & properties covered:
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {propertyTypes.map((type, idx) => <span key={type} data-index={idx} className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-md bg-muted text-foreground border border-border/60">
                  {type}
                </span>)}
            </div>
          </div>
        </motion.div>

        {/* Two main subsections: Residential & Commercial */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Subsection 1: Residential */}
          <motion.div variants={itemVariants}>
            <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                    <Home className="size-6" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Homeowners & Builders
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Residential scaffolding in Marlow
                </CardTitle>
                <CardDescription className="text-sm sm:text-base text-muted-foreground">
                  Careful, tidy scaffolding solutions for domestic properties. We safeguard gardens, driveways, and neighbouring boundaries with minimal disruption to your home routine.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-2.5">
                  {residentialFeatures.map((feature, i) => <li key={feature} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>)}
                </ul>

                <Separator />

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button asChild variant="default" size="sm">
                    <Link to="/ResidentialScaffoldingPage">
                      Residential scaffolding
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/ChimneyScaffoldingPage">
                      <Flame className="size-3.5 mr-1 text-primary" />
                      Chimney scaffolding
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Subsection 2: Commercial */}
          <motion.div variants={itemVariants}>
            <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                    <Building2 className="size-6" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Contractors & Developers
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Commercial scaffolding in Marlow
                </CardTitle>
                <CardDescription className="text-sm sm:text-base text-muted-foreground">
                  Engineered access systems designed to keep busy construction, refurbishment, and occupied retail or office sites operating smoothly and in full NASC and TG20 compliance.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-2.5">
                  {commercialFeatures.map((feature, i) => <li key={feature} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>)}
                </ul>

                <Separator />

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button asChild variant="default" size="sm">
                    <Link to="/CommercialScaffoldingPage">
                      Commercial scaffolding
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/MainContractorsDevelopersPage">
                      Main contractors & developers
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl bg-muted/60 border border-border p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-xs tracking-wider uppercase">
                <HardHat className="size-4" />
                Specialist Works
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight font-default">
                Roofing & trade access in Marlow
              </h3>
              <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                Dedicated working platforms, weather-tight temporary canopies, and edge protection systems configured for rapid installation and maximum trade productivity.
              </p>
            </div>
            <Button asChild variant="default" size="default" className="shrink-0 w-fit">
              <Link to="/ContactRequestQuotePage">
                Request site visit
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {tradeAccessCards.map((card, i) => {
            const IconComp = card.icon;
            return <div key={card.title} data-index={i} className="flex flex-col justify-between p-5 rounded-xl bg-card border border-border shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 rounded-md bg-muted text-primary">
                        <IconComp className="size-5" />
                      </div>
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {card.tag}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold text-foreground mb-1.5">
                      {card.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                      {card.description}
                    </p>
                  </div>
                  <Button asChild variant="link" size="sm" className="px-0 justify-start font-semibold text-primary">
                    <Link to={card.to}>
                      Explore {card.title.toLowerCase()}
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}